import React, { useState, useEffect } from 'react';
import {
  View,
  Text,
  StyleSheet,
  SafeAreaView,
  TouchableOpacity,
  Image,
  ScrollView,
  ActivityIndicator,
  Alert,
} from 'react-native';
import { ms, s, vs } from '../../../../utils/responsive';
import { useNavigation, useRoute, RouteProp } from '@react-navigation/native';
import { RootStackNavigationProp } from '../../../../types/navigation';
import { BottomNavBar } from '../../../../components/BottomNavBar';
import { medicalEquipmentService, MedicalEquipment } from '../../../../services/medicalEquipment';
import { tasksService } from '../../../../services/tasks';

import { createLogger } from '../../../../utils/logger';
const logger = createLogger('MedicalEquipmentData');

type RouteParams = {
  targetUserId?: string;
  viewOnly?: boolean;
};

type InfoItemProps = {
  label: string;
  value: string;
};

const InfoItem: React.FC<InfoItemProps> = ({ label, value }) => (
  <View style={styles.infoItem}>
    <Text style={styles.label}>{label}</Text>
    <Text style={styles.value}>{value || 'Not specified'}</Text>
    <View style={styles.separator} />
  </View>
);

export const MedicalEquipmentDataScreen: React.FC = () => {
  const navigation = useNavigation<RootStackNavigationProp>();
  const route = useRoute<RouteProp<Record<string, RouteParams>, string>>();
  const targetUserId = route.params?.targetUserId;
  const viewOnly = route.params?.viewOnly || false;

  const [medicalEquipmentData, setMedicalEquipmentData] = useState<MedicalEquipment[]>([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadMedicalEquipmentData();
  }, [targetUserId]);

  const loadMedicalEquipmentData = async () => {
    try {
      setLoading(true);
      // Pass targetUserId to fetch another user's data (for successor view)
      const response = await medicalEquipmentService.getMedicalEquipment(11, targetUserId); // Medical equipment task ID
      logger.debug('[Medical Equipment Data Debug] Loaded all medical equipment entries:', response, targetUserId ? `(viewing ${targetUserId})` : '');

      if (response && Array.isArray(response) && response.length > 0) {
        // Set all medical equipment entries
        logger.debug('[Medical Equipment Data Debug] Setting all medical equipment data entries:', response);
        setMedicalEquipmentData(response);
      } else {
        logger.debug('[Medical Equipment Data Debug] No medical equipment entries found or invalid data format');
        setMedicalEquipmentData([]);
      }
    } catch (error) {
      logger.error('Error loading medical equipment data:', error);
      Alert.alert('Error', 'Failed to load medical equipment information');
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (dateString: string) => {
    if (!dateString) return '';
    try {
      const date = new Date(dateString);
      return date.toLocaleDateString();
    } catch (error) {
      return dateString;
    }
  };

  const handleEditMedicalEquipment = (medicalEquipment: MedicalEquipment) => {
    const mappedData = {
      id: medicalEquipment.id,
      equipmentType: medicalEquipment.equipmentType,
      companyName: medicalEquipment.companyName,
      location: medicalEquipment.location,
      phone: medicalEquipment.phone,
      email: medicalEquipment.email,
      state: medicalEquipment.state,
      notes: medicalEquipment.notes,
      rentalCost: medicalEquipment.rentalCost,
      startDate: medicalEquipment.startDate,
      contractEnd: medicalEquipment.contractEnd,
      contactPerson: medicalEquipment.contactPerson,
      maintenanceSchedule: medicalEquipment.maintenanceSchedule,
      serialNumber: medicalEquipment.serialNumber,
      modelNumber: medicalEquipment.modelNumber,
    };
    navigation.navigate('MedicalEquipmentInput', {
      medicalEquipmentData: mappedData,
      isEditing: true
    });
  };

  const handleDeleteMedicalEquipment = async (medicalEquipment: MedicalEquipment) => {
    Alert.alert('Delete', `Are you sure you want to delete this medical equipment entry?`, [
      { text: 'Cancel', style: 'cancel' },
      {
        text: 'Delete',
        style: 'destructive',
        onPress: async () => {
          try {
            await medicalEquipmentService.deleteMedicalEquipment(medicalEquipment.id);

            // Reload data after deletion
            await loadMedicalEquipmentData();

            // Check if there are any remaining medical equipment entries
            const remainingData = await medicalEquipmentService.getMedicalEquipment(11);
            if (!remainingData || remainingData.length === 0) {
              await tasksService.resetTask('medical_equipment');
              navigation.navigate('MedicalChecklist');
            }

            Alert.alert('Deleted', 'Medical equipment information deleted.');
          } catch (e) {
            logger.error('Delete medical equipment failed:', e);
            Alert.alert('Error', 'Failed to delete medical equipment information');
          }
        }
      },
    ]);
  };

  const handleAddMedicalEquipment = () => {
    navigation.navigate('MedicalEquipmentInput');
  };

  if (loading) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Medical Equipment Information</Text>

        <View style={styles.whiteContainer}>
          <View style={styles.loadingContainer}>
            <ActivityIndicator size="large" color="#29BFC0" />
            <Text style={styles.loadingText}>Loading medical equipment information...</Text>
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  if (!medicalEquipmentData || medicalEquipmentData.length === 0) {
    return (
      <SafeAreaView style={styles.container}>
        <View style={styles.header}>
          <TouchableOpacity
            style={styles.backButton}
            onPress={() => navigation.goBack()}
          >
            <Image
              source={require('../../../../assets/icons/back.png')}
              style={styles.backIcon}
            />
          </TouchableOpacity>
          <Image
            source={require('../../../../assets/logo.png')}
            style={styles.logo}
            resizeMode="contain"
          />
          <TouchableOpacity style={styles.notificationButton}>
            <Image
              source={require('../../../../assets/icons/notification.png')}
              style={styles.notificationIcon}
            />
          </TouchableOpacity>
        </View>

        <Text style={styles.title}>Medical Equipment Information</Text>

        {viewOnly && (
          <View style={styles.viewOnlyBanner}>
            <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
          </View>
        )}

        <View style={styles.whiteContainer}>
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>
              {viewOnly ? 'This user has not added medical equipment information yet' : 'No medical equipment information found'}
            </Text>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addButton}
                onPress={handleAddMedicalEquipment}
              >
                <Text style={styles.addButtonText}>Add Medical Equipment Info</Text>
              </TouchableOpacity>
            )}
          </View>
        </View>
        <BottomNavBar />
      </SafeAreaView>
    );
  }

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <TouchableOpacity
          style={styles.backButton}
          onPress={() => navigation.goBack()}
        >
          <Image
            source={require('../../../../assets/icons/back.png')}
            style={styles.backIcon}
          />
        </TouchableOpacity>
        <Image
          source={require('../../../../assets/logo.png')}
          style={styles.logo}
          resizeMode="contain"
        />
        <TouchableOpacity style={styles.notificationButton}>
          <Image
            source={require('../../../../assets/icons/notification.png')}
            style={styles.notificationIcon}
          />
        </TouchableOpacity>
      </View>

      <Text style={styles.title}>Medical Equipment Information</Text>

      {viewOnly && (
        <View style={styles.viewOnlyBanner}>
          <Text style={styles.viewOnlyText}>View Only - You are viewing another user's data</Text>
        </View>
      )}

      <View style={styles.whiteContainer}>
        <ScrollView style={styles.scrollView}>
          {medicalEquipmentData.map((medicalEquipment, index) => (
            <View key={medicalEquipment.id || index} style={styles.medicalEquipmentCard}>
              <View style={styles.cardHeader}>
                <View style={{flex: 1, flexShrink: 1}}>
                  <Text style={styles.cardName}>
                    {medicalEquipment.equipmentType || 'Medical Equipment'}
                  </Text>
                  <Text style={styles.cardType}>
                    {medicalEquipment.companyName || 'Company not specified'}
                  </Text>
                </View>
                {!viewOnly && (
                  <View style={styles.cardButtons}>
                    <TouchableOpacity
                      style={styles.editCardButton}
                      onPress={() => handleEditMedicalEquipment(medicalEquipment)}
                    >
                      <Text style={styles.editCardButtonText}>Edit</Text>
                    </TouchableOpacity>
                    <TouchableOpacity
                      style={styles.deleteCardButton}
                      onPress={() => handleDeleteMedicalEquipment(medicalEquipment)}
                    >
                      <Text style={styles.deleteCardButtonText}>Delete</Text>
                    </TouchableOpacity>
                  </View>
                )}
              </View>

              <InfoItem label="Equipment Type" value={medicalEquipment.equipmentType || ''} />
              <InfoItem label="Company Name" value={medicalEquipment.companyName || ''} />
              <InfoItem label="Location" value={medicalEquipment.location || ''} />
              <InfoItem label="Phone" value={medicalEquipment.phone || ''} />
              <InfoItem label="Email" value={medicalEquipment.email || ''} />
              <InfoItem label="State" value={medicalEquipment.state || ''} />
              {medicalEquipment.rentalCost && (
                <InfoItem label="Rental Cost" value={medicalEquipment.rentalCost} />
              )}
              {medicalEquipment.startDate && (
                <InfoItem label="Start Date" value={formatDate(medicalEquipment.startDate)} />
              )}
              {medicalEquipment.contractEnd && (
                <InfoItem label="Contract End" value={formatDate(medicalEquipment.contractEnd)} />
              )}
              {medicalEquipment.contactPerson && (
                <InfoItem label="Contact Person" value={medicalEquipment.contactPerson} />
              )}
              {medicalEquipment.maintenanceSchedule && (
                <InfoItem label="Maintenance Schedule" value={medicalEquipment.maintenanceSchedule} />
              )}
              {medicalEquipment.serialNumber && (
                <InfoItem label="Serial Number" value={medicalEquipment.serialNumber} />
              )}
              {medicalEquipment.modelNumber && (
                <InfoItem label="Model Number" value={medicalEquipment.modelNumber} />
              )}
              {medicalEquipment.notes && (
                <InfoItem label="Notes" value={medicalEquipment.notes} />
              )}
            </View>
          ))}

          <View style={styles.buttonContainer}>
            {!viewOnly && (
              <TouchableOpacity
                style={styles.addAnotherMedicalEquipmentButton}
                onPress={handleAddMedicalEquipment}
              >
                <Text style={styles.addAnotherMedicalEquipmentButtonText}>Add Another Medical Equipment</Text>
              </TouchableOpacity>
            )}

            <TouchableOpacity
              style={[styles.button, styles.backToListButton]}
              onPress={() => navigation.navigate('MedicalChecklist')}
            >
              <Text style={styles.buttonText}>Back to Checklist</Text>
            </TouchableOpacity>
          </View>
        </ScrollView>
      </View>

      <BottomNavBar />
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#29BFC0',
  },
  header: {
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(15),
    paddingTop: ms(50),
    position: 'relative',
  },
  backButton: {
    position: 'absolute',
    left: ms(50),
    top: ms(15),
    padding: ms(5),
    zIndex: 1,
  },
  backIcon: {
    width: s(32),
    height: s(32),
    tintColor: '#333',
  },
  logo: {
    width: s(125),
    height: s(125),
  },
  notificationButton: {
    position: 'absolute',
    right: ms(15),
    top: '50%',
    transform: [{ translateY: -12 }],
    padding: ms(5),
  },
  notificationIcon: {
    width: s(24),
    height: s(24),
    tintColor: '#333',
  },
  title: {
    fontSize: ms(24, 0.3),
    fontWeight: '600',
    color: '#FFFFFF',
    textAlign: 'center',
    marginVertical: ms(20),
  },
  whiteContainer: {
    flex: 1,
    backgroundColor: '#FFFFFF',
    borderTopLeftRadius: ms(30),
    borderTopRightRadius: ms(30),
    paddingTop: ms(20),
  },
  scrollView: {
    flex: 1,
    paddingHorizontal: ms(20),
  },
  loadingContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  loadingText: {
    fontSize: ms(16, 0.3),
    color: '#29BFC0',
    marginTop: ms(10),
  },
  emptyContainer: {
    flex: 1,
    alignItems: 'center',
    justifyContent: 'center',
    padding: ms(40),
  },
  emptyText: {
    fontSize: ms(18, 0.3),
    color: '#6B7280',
    textAlign: 'center',
    marginBottom: ms(20),
  },
  addButton: {
    backgroundColor: '#29BFC0',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addButtonText: {
    color: '#FFFFFF',
    fontSize: ms(16, 0.3),
    fontWeight: '600',
  },
  medicalEquipmentCard: {
    backgroundColor: '#F8F9FA',
    borderRadius: ms(15),
    padding: ms(20),
    marginBottom: ms(20),
    borderWidth: ms(1),
    borderColor: '#E5E5E5',
  },
  cardHeader: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    alignItems: 'flex-start',
    marginBottom: ms(20),
    paddingBottom: ms(15),
    borderBottomWidth: ms(2),
    borderBottomColor: '#29BFC0',
  },
  cardName: {
    fontSize: ms(20, 0.3),
    fontWeight: '600',
    color: '#1C2C4F',
    marginBottom: ms(4),
  },
  cardType: {
    fontSize: ms(16, 0.3),
    color: '#6B7280',
  },
  cardButtons: {
    flexDirection: 'row',
    gap: ms(8),
  },
  editCardButton: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  editCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  deleteCardButton: {
    backgroundColor: '#DC3545',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    borderRadius: ms(15),
  },
  deleteCardButtonText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
  },
  infoItem: {
    marginBottom: ms(15),
  },
  label: {
    fontSize: ms(14, 0.3),
    color: '#6B7280',
    marginBottom: ms(5),
  },
  value: {
    fontSize: ms(16, 0.3),
    color: '#1C2C4F',
    marginBottom: ms(8),
  },
  separator: {
    height: s(1),
    backgroundColor: '#E5E5E5',
  },
  buttonContainer: {
    padding: ms(20),
    paddingBottom: ms(120),
    gap: ms(15),
  },
  addAnotherMedicalEquipmentButton: {
    backgroundColor: '#F2994A',
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  addAnotherMedicalEquipmentButtonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  button: {
    padding: ms(15),
    borderRadius: ms(25),
    alignItems: 'center',
  },
  backToListButton: {
    backgroundColor: '#29BFC0',
  },
  buttonText: {
    color: '#FFFFFF',
    fontSize: ms(18, 0.3),
    fontWeight: '600',
  },
  viewOnlyBanner: {
    backgroundColor: '#FF9F5A',
    paddingVertical: ms(8),
    paddingHorizontal: ms(15),
    marginHorizontal: ms(20),
    borderRadius: ms(8),
    marginBottom: ms(10),
  },
  viewOnlyText: {
    color: '#FFFFFF',
    fontSize: ms(14, 0.3),
    fontWeight: '600',
    textAlign: 'center',
  },
});
